import React from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"

import { useProductPositionFormCoreContext } from "../contexts/ProductPositionFormCoreContext"
import { useProductPositionFormFeaturesContext } from "../contexts/ProductPositionFormFeaturesContext"
import { useProductPositionFormLoadingContext } from "../contexts/ProductPositionFormLoadingContext"
import { ProductPositionInstallationReferenceList } from "./product-position-installation-reference-list/ProductPositionInstallationReferenceList"

export const ProductPositionInstallationReferences = React.memo(() => {
  const { t } = useTranslation()
  const { isReadonly, permissions, formOptions } =
    useProductPositionFormCoreContext()
  const { isLoading, isLoadingInstallationReferences } =
    useProductPositionFormLoadingContext()
  const {
    productPositionInstallationReferences,
    installationReferences,
    openInstallationReferenceSelection,
    deleteInstallationReference
  } = useProductPositionFormFeaturesContext()
  const {
    hideInstallationReferences,
    hideInstallationReferenceSelectionLink,
    hideInstallationReferenceSelectionWhileEditing
  } = formOptions || {}

  const references = useWatch({ name: "productPosition.references" })

  if (hideInstallationReferences || !permissions[ProjectPermissionType.EDIT]) {
    return null
  }

  const showEmptyInstallationReferences = references?.length === 0
  const showSelectionLink =
    openInstallationReferenceSelection &&
    !hideInstallationReferenceSelectionLink &&
    (isReadonly || !hideInstallationReferenceSelectionWhileEditing) &&
    permissions[ProjectPermissionType.EDIT]

  return (
    <div
      className={classNames("product-position-form__install-ref-select", {
        "no-references": showEmptyInstallationReferences
      })}
    >
      <ProductPositionInstallationReferenceList
        productPositionInstallationReferences={
          productPositionInstallationReferences
        }
        installationReferences={installationReferences}
        isEditing={!isReadonly}
        isLoading={isLoading || isLoadingInstallationReferences}
        onDeleteInstallationReference={
          permissions[ProjectPermissionType.EDIT]
            ? deleteInstallationReference
            : undefined
        }
      />
      {showSelectionLink ? (
        <Button
          id="select-installation-references-button"
          data-testid="select-installation-references-button"
          variant={ButtonVariant.PRIMARY}
          className="select-installation-references-button"
          onClick={openInstallationReferenceSelection}
          icon={IconName.BUILDING}
        >
          {t("InstallationReference.SelectInstallationReferences")}
        </Button>
      ) : null}
    </div>
  )
})
